import {
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  BookOpen,
  CircleHelp,
  CreditCard,
  LockKeyhole,
  Mail,
  MessageSquareText,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useEffect, type PropsWithChildren, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Brand } from "../../components/Brand";
import { applyPageMetadata } from "../../lib/seo";

const supportEmail = "steriahouse@gmail.com";
const supportMailto = `mailto:${supportEmail}?subject=${encodeURIComponent("N1 Master Support")}`;
const n1MasterPublicUrl = "https://steriahouse.web.app";

function N1MasterPageFrame({ children }: PropsWithChildren) {
  return (
    <div className="n1-page">
      <header className="n1-nav">
        <Link className="n1-nav-brand" to="/" aria-label="Steria House home">
          <Brand compact />
          <span className="n1-nav-divider" aria-hidden="true" />
          <span>N1 Master</span>
        </Link>
        <Link className="n1-back-link" to="/projects/n1-master">
          <ArrowLeft size={16} /> Product overview
        </Link>
      </header>
      {children}
      <footer className="n1-footer">
        <span>© {new Date().getFullYear()} N1 Master</span>
        <div>
          <Link to="/apps/n1master/privacy">Privacy Policy</Link>
          <Link to="/apps/n1master/terms">Terms of Service</Link>
          <Link to="/apps/n1master/support">Support</Link>
        </div>
      </footer>
    </div>
  );
}

function SupportCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <article className="n1-support-card">
      <span className="n1-support-icon">{icon}</span>
      <h2>{title}</h2>
      <div>{children}</div>
    </article>
  );
}

export function N1MasterSupportPage() {
  useEffect(() => {
    applyPageMetadata({
      title: "N1 Master Support | Steria House",
      description: "Get help with N1 Master accounts, progress, purchases, and technical issues.",
      canonicalPath: "/apps/n1master/support",
    });
  }, []);

  return (
    <N1MasterPageFrame>
      <main>
        <section className="n1-hero">
          <div className="n1-orbit n1-orbit-one" aria-hidden="true" />
          <div className="n1-orbit n1-orbit-two" aria-hidden="true" />
          <div className="n1-hero-content">
            <span className="n1-kicker"><Sparkles size={15} /> N1 Master support</span>
            <h1>We are here to help you keep learning.</h1>
            <p>Get help with your account, learning progress, PRO access, purchases, or a problem in the app.</p>
            <a className="button button-primary" href={supportMailto}>
              <Mail size={17} /> Contact support <ArrowUpRight size={16} />
            </a>
          </div>
          <aside className="n1-response-card">
            <MessageSquareText size={23} />
            <strong>Send a clear report</strong>
            <p>Include the app version, device model, iOS version, and a screenshot or screen recording when possible.</p>
          </aside>
        </section>

        <section className="n1-support-section">
          <div className="n1-section-heading">
            <span>COMMON TOPICS</span>
            <h2>Start with the answer you need.</h2>
          </div>
          <div className="n1-support-grid">
            <SupportCard icon={<LockKeyhole size={21} />} title="Account and sign-in"><p>Use the same sign-in method you used before. If Google or Apple sign-in does not complete, restart the app and try again on a stable connection.</p></SupportCard>
            <SupportCard icon={<CreditCard size={21} />} title="PRO and payments"><p>Subscriptions are managed by Apple. Review, cancel, or change a plan in Apple ID subscriptions. Use Restore Purchases after reinstalling or changing devices.</p></SupportCard>
            <SupportCard icon={<RefreshCcw size={21} />} title="Progress and sync"><p>Sign in with the same account and connect to the internet to sync progress. Tell us which feature, level, or activity is missing if the issue remains.</p></SupportCard>
            <SupportCard icon={<BadgeCheck size={21} />} title="Rewards and energy"><p>If a verified purchase or rewarded ad did not grant its reward, include the approximate time, product name, and any Apple transaction details available to you.</p></SupportCard>
            <SupportCard icon={<BookOpen size={21} />} title="Learning content"><p>Report a typo, unclear answer, translation issue, or content suggestion. Include the JLPT level and a screenshot so we can find the exact material.</p></SupportCard>
            <SupportCard icon={<CircleHelp size={21} />} title="Technical issues"><p>For crashes, blank screens, sound problems, or incorrect layouts, send the steps that reproduce the issue and attach a screenshot or recording if possible.</p></SupportCard>
          </div>
        </section>

        <section className="n1-help-strip">
          <ShieldCheck size={28} />
          <div>
            <h2>Privacy or account data request</h2>
            <p>For access, correction, or deletion requests, email us with the subject <strong>N1 Master Privacy Request</strong>.</p>
          </div>
          <div className="n1-help-actions">
            <Link to="/apps/n1master/privacy">Privacy Policy</Link>
            <Link to="/apps/n1master/terms">Terms of Service</Link>
          </div>
        </section>
      </main>
    </N1MasterPageFrame>
  );
}

type LegalDocument = "privacy" | "terms";

const privacySections: Array<{ title: string; body: ReactNode }> = [
  {
    title: "1. Information We Collect",
    body: <><p>We may process account information supplied through Firebase Authentication, Google Sign-In, or Sign in with Apple, including a Firebase user ID, display name, email address, and profile image URL when the provider supplies it. Guest sessions use a pseudonymous Firebase user ID.</p><p>We also process settings, learning progress, scores, streaks, energy, PRO status, certificates, achievements, custom flashcards, feedback, device and app information, diagnostics, security signals, and locally stored settings or downloaded content needed for the Service and PRO offline access.</p><p>Apple processes iOS payment details. We receive only information needed to verify and deliver purchases, such as product and transaction identifiers, entitlement status, expiry, revocation status, and an account token. We do not receive or store full payment-card details.</p></>,
  },
  {
    title: "2. How We Use Information",
    body: <><p>We use information to authenticate accounts, save and sync learning progress, operate the energy, achievement, PRO, leaderboard, and purchase systems, respond to support requests, prevent fraud and duplicate rewards, diagnose errors, and comply with legal obligations.</p><p>Eligible non-PRO users may see banner, interstitial, or rewarded ads through Google Mobile Ads. Where required, we present consent choices and Apple’s App Tracking Transparency prompt before accessing the advertising identifier for tracking.</p></>,
  },
  {
    title: "3. How We Share Information",
    body: <><p>We do not sell personal information. We share information only as needed to operate the Service, comply with law, or protect rights and safety.</p><ul><li><strong>Google Firebase and Google Cloud:</strong> authentication, cloud synchronization, account data, secure backend processing, and fraud prevention.</li><li><strong>Apple:</strong> App Store purchases, subscriptions, refund or revocation status, and Sign in with Apple.</li><li><strong>Google Mobile Ads / AdMob:</strong> eligible advertising delivery, measurement, frequency control, and rewarded-ad verification.</li><li><strong>Service providers and authorities:</strong> only where needed to operate the Service or when legally required.</li></ul></>,
  },
  {
    title: "4. Retention, Security, and Your Choices",
    body: <><p>We retain data only as long as reasonably necessary to provide the Service, preserve learning history, resolve support issues, prevent fraud, meet legal obligations, and enforce our agreements. We use reasonable technical and organizational safeguards, but no system is completely secure.</p><p>You may update information available in the app, sign out, and manage subscriptions through your Apple ID subscription settings. You can also delete your account and associated data directly within the N1 Master mobile application under Profile &gt; Delete Account. To request access, correction, deletion, or information about our processing, email <a href={supportMailto}>{supportEmail}</a> with the subject <strong>N1 Master Privacy Request</strong>. We may verify that you control the account before completing a request.</p></>,
  },
  {
    title: "5. Children, International Processing, and Changes",
    body: <p>N1 Master is not directed to children under 13. We do not knowingly collect personal information from children under 13. The Service and its providers may process information in countries other than where you live, with appropriate safeguards where required. We may update this policy as the Service or applicable law changes and will revise the date above.</p>,
  },
];

const termsSections: Array<{ title: string; body: ReactNode }> = [
  {
    title: "1. The Service",
    body: <p>N1 Master provides Japanese-language learning tools and JLPT practice content, including learning materials, training sessions, mock exams, games, conversations, challenges, flashcards, and a guided learning path. It is an independent learning aid and not affiliated with The Japan Foundation, JEES, or any official JLPT organization. We do not guarantee a particular examination result, language level, or learning outcome.</p>,
  },
  {
    title: "2. Accounts and Acceptable Use",
    body: <><p>You are responsible for information associated with your account and for keeping your device and sign-in credentials secure. Do not impersonate another person, bypass purchase, energy, reward, advertising, or security controls; manipulate progress or rankings; use bots or scraping; interfere with the Service; or submit unlawful, abusive, fraudulent, or infringing content. We strictly prohibit objectionable or abusive content, bullying, harassment, and any exploitation of children or trafficking. Violations may result in immediate suspension or termination of account access.</p><p>We grant a limited, personal, non-exclusive, non-transferable, revocable license to use the Service for personal, non-commercial learning. The Service and its content remain owned by N1 Master or its licensors.</p></>,
  },
  {
    title: "3. iOS PRO, Energy, and Purchases",
    body: <><p>Apple processes iOS purchases. Prices, currencies, renewal terms, trials, and applicable taxes are shown by Apple at checkout.</p><ul><li><strong>Monthly PRO</strong> (<code>monthly</code>) is an auto-renewable monthly subscription.</li><li><strong>Annual PRO</strong> (<code>annual</code>) is an auto-renewable annual subscription.</li><li>Basic users can earn Energy only through server-verified rewarded ads. N1 Master does not sell Energy on iOS.</li></ul><p>Subscriptions renew automatically unless cancelled at least 24 hours before the current period ends. You can manage subscriptions through your Apple ID subscription settings. Use Restore Purchases to restore an active subscription after reinstalling or changing devices. Apple handles billing, cancellations, and refunds under its policies and applicable law. Subscriptions are also subject to Apple’s standard Licensed Application End User License Agreement (EULA) at <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer">Apple Standard EULA</a>.</p></>,
  },
  {
    title: "4. Disclaimers and Liability",
    body: <p>To the extent permitted by law, the Service is provided “AS IS” and “AS AVAILABLE.” We do not guarantee uninterrupted, error-free, complete, or continuously available Service. To the fullest extent permitted by law, we are not liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or losses of data, profits, goodwill, or learning outcomes. Mandatory consumer rights remain unaffected.</p>,
  },
  {
    title: "5. Changes and Contact",
    body: <p>We may update the Service, its content, features, pricing, or these Terms. Continued use after revised Terms take effect means you accept them when permitted by law. These Terms are governed by the laws of the Republic of Indonesia, except where mandatory local law provides otherwise. Questions may be sent to <a href={supportMailto}>{supportEmail}</a>.</p>,
  },
];

export function N1MasterLegalPage({ document }: { document: LegalDocument }) {
  const isPrivacy = document === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "Terms of Service";
  const description = isPrivacy
    ? "How N1 Master handles account, learning, purchase, and advertising information."
    : "The terms that govern the use of N1 Master and its iOS purchases.";
  const sections = isPrivacy ? privacySections : termsSections;

  useEffect(() => {
    applyPageMetadata({
      title: `${title} | N1 Master`,
      description,
      canonicalPath: `/apps/n1master/${document}`,
      ...(isPrivacy ? { siteUrl: n1MasterPublicUrl } : {}),
    });
  }, [description, document, isPrivacy, title]);

  return (
    <N1MasterPageFrame>
      <main className="n1-legal">
        <header><span className="n1-kicker">N1 MASTER · LEGAL</span><h1>{title}</h1><p>Last updated: October 18, 2026</p></header>
        <article>
          <p className="n1-legal-intro">This document applies to the N1 Master mobile application and related support services.</p>
          {sections.map((section) => <section key={section.title}><h2>{section.title}</h2>{section.body}</section>)}
          <section>
            <h2>Contact Us</h2>
            <p>If you have any questions regarding this {isPrivacy ? "Privacy Policy or our data practices" : "Terms of Service or the App"}, please contact us at:</p>
            <p style={{ marginTop: "0.5rem", lineHeight: 1.6 }}>
              <strong>Application Name:</strong> N1 Master<br />
              <strong>User Support Email:</strong> <a href="mailto:muhamadwildanfaz@gmail.com">muhamadwildanfaz@gmail.com</a><br />
              <strong>Developer Contact Email:</strong> <a href="mailto:muhamadwildanfaz@gmail.com">muhamadwildanfaz@gmail.com</a><br />
              <strong>Alternate Support Email:</strong> <a href="mailto:steriahouse@gmail.com">steriahouse@gmail.com</a>
            </p>
          </section>
        </article>
      </main>
    </N1MasterPageFrame>
  );
}
